import React, { useState, useEffect } from 'react';
import DetailedPageLayout from '../components/DetailedPageLayout';
import CustomAreaChart from '../../../components/Charts/CustomAreaChart';
import CustomBarChart from '../../../components/Charts/CustomBarChart';
import CustomDonutChart from '../../../components/Charts/CustomDonutChart';
import dashboardAPI from '../../../services/dashboardService';
import { Users, FileText, Calendar, Activity } from 'lucide-react';

const StatCard = ({ title, value, icon: Icon, color }) => (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4">
        <div className={`p-3 rounded-lg ${color}`}>
            <Icon size={24} className="text-white" />
        </div>
        <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
    </div>
);

const DetailedOverviewPage = ({ isSubView = false, filters: externalFilters, onFilterChange: externalOnFilterChange }) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [localFilters, setLocalFilters] = useState({ timeRange: '30days', category: 'all' });

    const filters = externalFilters || localFilters;
    const onFilterChange = externalOnFilterChange || ((newFilters) => {
        setLocalFilters(newFilters);
        fetchStats(newFilters);
    });

    const fetchStats = async (newFilters = filters) => {
        setLoading(true);
        try {
            const response = await dashboardAPI.getOverviewStats(newFilters);
            setStats(response.data);
        } catch (error) {
            console.error("Failed to fetch overview stats", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStats(filters);
    }, [filters]);

    if (loading || !stats) {
        return <div className="p-8 text-center text-gray-500">Loading statistics...</div>;
    }

    const deviceData = stats.graph_data?.device_stats || [];
    const growthData = stats.graph_data?.visitor_growth || [];

    return (
        <DetailedPageLayout
            title="System Overview"
            onFilterChange={onFilterChange}
            filters={filters}
            isSubView={isSubView}
        >
            {/* Top Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                <StatCard
                    title="Total Users"
                    value={stats.totalUsers}
                    icon={Users}
                    color="bg-blue-500"
                />
                <StatCard
                    title="Total Posts"
                    value={stats.totalPosts}
                    icon={FileText}
                    color="bg-purple-500"
                />
                <StatCard
                    title="Appointments"
                    value={stats.totalAppointments}
                    icon={Calendar}
                    color="bg-red-500"
                />
                <StatCard
                    title="Active Clinics"
                    value={stats.activeClinics}
                    icon={Activity}
                    color="bg-teal-500"
                />
            </div>

            {/* Charts Row 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                    <CustomAreaChart
                        data={growthData}
                        color="#3b82f6"
                        title="Visitor Growth"
                    />
                </div>
                <div className="lg:col-span-1">
                    <CustomDonutChart
                        data={deviceData}
                        totalValue={deviceData.reduce((a, b) => a + b.value, 0)}
                        title="Device Usage"
                    />
                </div>
            </div>
        </DetailedPageLayout>
    );
};

export default DetailedOverviewPage;
